/**
 * learning/dialogs.tsx — the Learning Hub's three dialogs: the FAQ, the self-assessment and the
 * AI Awareness training, plus the shared dialog chrome they are built from.
 *
 * WHY THEY LIVE HERE. They were inline in LearningHub.tsx. The Kombi Learning page needs the SAME
 * three dialogs, and there were only two other options: import them from the page that is being
 * replaced (which couples the new surface to the archived one, and deletes the dialogs the day that
 * page goes), or copy them (a second implementation of a self-assessment that writes a person's
 * competency level, drifting the moment either side is touched). So they moved here and BOTH pages
 * import them. LearningHub.tsx keeps rendering exactly these components.
 *
 * THEY ARE NOT KOMBI, DELIBERATELY, AND THAT IS WHY THE KOMBI PAGE MOUNTS THEM OUTSIDE `.kombi-app`.
 * They are Tailwind/design-system components (OverlayFrame, ModalShell, Button, Pill). Kombi scopes
 * every font and colour to `.kombi-app` and SHADOWS `--ink` with a different value inside it, while
 * Tailwind's Preflight is global — so rendering one of these inside the wrapper would silently repaint
 * it in Kombi's palette and typeface. They are fixed-position centred overlays, so mounting them as a
 * SIBLING of the wrapper costs nothing visually. Same trade, and the same reasoning, as the eleven
 * admin panels in KombiAdmin.tsx.
 */
import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { AnimatePresence } from 'motion/react';
import { Button } from '../tools/upload/design/Button';
import { ModalShell } from '../tools/upload/design/ModalShell';
import { OverlayFrame } from '../tools/upload/design/OverlayFrame';
import { Pill } from '../tools/upload/design/Pill';
import { useLearningModal } from './useLearningModal';
import { ABOUT_FAQS, ASSESSMENT_QUESTIONS, deriveLevel, MAKER_TYPES } from './dialogContent';
import {
  completeStep,
  getTraining,
  submitKnowledgeCheck,
  submitSelfAssessment,
  type CompetencyLevel,
  type MakerType,
  type TrainingPayload,
} from './api';

// LEVELS / MAKER_TYPES and the dialog content now live in dialogContent.tsx, shared with the Kombi
// dialogs. Re-exported because the pages import LEVELS from here.
export { LEVELS, MAKER_TYPES } from './dialogContent';

/**
 * The modal title, matching the upload flow's own (FlowPickerReskin's P1 header) exactly — 24px/300
 * on `--text`, `min-w-0` so a long title truncates rather than pushing the header's right-hand chip
 * off the row. Deliberately no `leading-*`: the finished modals do not set one either.
 */
const MODAL_TITLE = 'min-w-0 text-xl font-light text-text';

// ── the three dialogs ────────────────────────────────────────────────────────────────────────

/**
 * The footer row every Learning dialog shares, copied from the upload flow's own non-wizard modal
 * (FlowPickerReskin's P1): a full-width hairline, then the actions on the modal's horizontal inset
 * with the right-hand group pulled in by `pr-modal-footer-r`. `left` takes a Back-style control; the
 * children are the primary group, pinned right.
 */
function DialogFooter({ left, children }: { left?: ReactNode; children: ReactNode }) {
  return (
    <div className="border-t border-ink">
      {/* `gap-7` (16px), not `gap-modal-footer` (15px): the outer row is verbatim
          UploadSelectPage.tsx:251 / FlowPickerReskin.tsx:203. The two inner clusters below
          keep `gap-modal-footer`, which is what those references use for them. */}
      <div className="flex flex-wrap items-center justify-between gap-7 px-modal-x py-11 pr-modal-footer-r">
        <div className="flex items-center gap-modal-footer">{left}</div>
        <div className="flex items-center gap-modal-footer">{children}</div>
      </div>
    </div>
  );
}

/** The dialog body's own vertical rhythm — the header and footer own their insets, the body owns this. */
const DIALOG_BODY = 'grid gap-8 pt-11 pb-11';

/** An error the dialog surfaces in place, announced immediately. */
function DialogError({ message }: { message: string }) {
  return (
    <p role="alert" className="text-xs font-light text-error">
      {message}
    </p>
  );
}

export function FaqDialog({
  onClose,
  returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: RefObject<HTMLButtonElement>;
}) {
  const { dialogRef, onKeyDown } = useLearningModal(onClose, returnFocusRef);

  return (
    <OverlayFrame dialogRef={dialogRef} onKeyDown={onKeyDown} onClose={onClose}>
      <AnimatePresence mode="wait" initial={false}>
        <ModalShell
          key="faq"
          labelledBy="faq-title"
          headerInset="title"
          header={
            <h1 id="faq-title" className={MODAL_TITLE}>
              About Trailblazer
            </h1>
          }
          footer={
            <DialogFooter>
              <Button variant="outline-square" size="xs" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          }
        >
          {/* A <dl> because this genuinely is a term/description list: assistive technology announces
              each answer as belonging to its question, without inventing heading levels for prose. */}
          <dl className={DIALOG_BODY}>
            {ABOUT_FAQS.map((item) => (
              <div key={item.q}>
                <dt className="text-sm font-medium text-ink">{item.q}</dt>
                <dd className="mt-1 max-w-[70ch] text-xs font-light text-text">{item.a}</dd>
              </div>
            ))}
          </dl>
        </ModalShell>
      </AnimatePresence>
    </OverlayFrame>
  );
}

function RadioGroup({
  name,
  legend,
  options,
}: {
  name: string;
  legend: string;
  options: { label: string; checked: boolean; onSelect: () => void }[];
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="text-sm font-medium text-ink">{legend}</legend>
      <div className="mt-3 grid gap-1">
        {options.map((opt) => (
          <label
            key={opt.label}
            className="flex cursor-pointer items-center gap-3 text-xs font-light text-text"
          >
            <input type="radio" name={name} checked={opt.checked} onChange={opt.onSelect} />
            {opt.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function AssessmentDialog({
  currentLevel,
  currentMakerType,
  onClose,
  onAssessed,
  returnFocusRef,
}: {
  currentLevel: CompetencyLevel | null;
  currentMakerType: MakerType | null;
  onClose: () => void;
  onAssessed: () => void;
  returnFocusRef: RefObject<HTMLButtonElement>;
}) {
  const { dialogRef, onKeyDown } = useLearningModal(onClose, returnFocusRef);
  const [answers, setAnswers] = useState<Record<string, CompetencyLevel>>({});
  const [makerType, setMakerType] = useState<MakerType | null>(currentMakerType);
  const [phase, setPhase] = useState<'questions' | 'result'>('questions');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = ASSESSMENT_QUESTIONS.map((q) => answers[q.id]).filter((l): l is CompetencyLevel =>
    Boolean(l),
  );
  const allAnswered = chosen.length === ASSESSMENT_QUESTIONS.length;
  const suggested = deriveLevel(chosen);

  const save = async () => {
    setSaving(true);
    setError(null);
    const res = await submitSelfAssessment(suggested, makerType ?? undefined);
    setSaving(false);
    if (res.kind === 'ok') {
      onAssessed();
    } else if (res.kind === 'unauthorized') {
      setError('Your session has expired. Please sign in again.');
    } else {
      setError('Could not save your self-assessment. Please try again.');
    }
  };

  return (
    <OverlayFrame dialogRef={dialogRef} onKeyDown={onKeyDown} onClose={onClose}>
      {/* `mode="wait"` so the two phases cross-fade in place, the way the wizard's steps do — only
          one shell is ever mounted, so their heading ids never collide. */}
      <AnimatePresence mode="wait" initial={false}>
        {phase === 'questions' ? (
          <ModalShell
            key="questions"
            labelledBy="assess-title"
            headerInset="title"
            header={
              <h1 id="assess-title" className={MODAL_TITLE}>
                Self-assessment
              </h1>
            }
            footer={
              <DialogFooter>
                <Button variant="outline-square" size="xs" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="outline-square"
                  size="xs"
                  state={allAnswered ? 'default' : 'disabled'}
                  onClick={() => setPhase('result')}
                >
                  See my result
                </Button>
              </DialogFooter>
            }
          >
            <div className={DIALOG_BODY}>
              {ASSESSMENT_QUESTIONS.map((q, i) => (
                <RadioGroup
                  key={q.id}
                  name={q.id}
                  legend={`${i + 1}. ${q.prompt}`}
                  options={q.options.map((opt) => ({
                    label: opt.label,
                    checked: answers[q.id] === opt.level,
                    onSelect: () => setAnswers((a) => ({ ...a, [q.id]: opt.level })),
                  }))}
                />
              ))}
              <RadioGroup
                name="makerType"
                legend="How you work (optional)"
                options={MAKER_TYPES.map((mt) => ({
                  label: mt,
                  checked: makerType === mt,
                  onSelect: () => setMakerType(mt),
                }))}
              />
            </div>
          </ModalShell>
        ) : (
          <ModalShell
            key="result"
            labelledBy="assess-result-title"
            headerInset="title"
            header={
              <h1 id="assess-result-title" className={MODAL_TITLE}>
                Start at {suggested}
              </h1>
            }
            footer={
              <DialogFooter
                left={
                  <Button variant="outline-square" size="xs" onClick={() => setPhase('questions')}>
                    Back
                  </Button>
                }
              >
                <Button
                  variant="outline-square"
                  size="xs"
                  state={saving ? 'disabled' : 'default'}
                  onClick={() => void save()}
                >
                  {saving ? 'Saving…' : 'Save my level'}
                </Button>
              </DialogFooter>
            }
          >
            <div className={DIALOG_BODY}>
              {/* The powerless invariant, stated where the result is shown (LH-F09 / LH-NFR04). */}
              <p className="max-w-[70ch] text-xs font-light text-text">
                Self-assessed, and re-takeable any time. It changes nothing about your access.
                {currentLevel ? ` You are currently ${currentLevel}.` : ''}
              </p>
              {error && <DialogError message={error} />}
            </div>
          </ModalShell>
        )}
      </AnimatePresence>
    </OverlayFrame>
  );
}

// ── mandatory-training dialog ────────────────────────────────────────────────────────────────

/**
 * WHY NOT `StepPills` IN THE HEADER. The wizard's step pills are laid out on `--step-gap` (94px),
 * measured so exactly FOUR pills fill the modal's 926px header row (1056 − 2 × 65). Training has
 * seven steps plus a knowledge check; even five short pills overflow that row, and `StepPills`'
 * `overflow-clip` would truncate them visibly. So this dialog wears the same `headerInset="title"`
 * header the other two do, with the position stated in one right-pinned Pill instead.
 *
 * Each position renders its OWN ModalShell, keyed, inside one AnimatePresence — the arrangement the
 * upload wizard's pages use — so moving between steps cross-fades and each step reports its own
 * accessible name. Heading ids are per-step for the same reason.
 */
export function TrainingDialog({
  onClose,
  onProgress,
  returnFocusRef,
}: {
  onClose: () => void;
  onProgress: () => void;
  returnFocusRef: RefObject<HTMLButtonElement>;
}) {
  const { dialogRef, onKeyDown } = useLearningModal(onClose, returnFocusRef);
  const [payload, setPayload] = useState<TrainingPayload | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Step index (0-based) or 'check' for the knowledge check.
  const [pos, setPos] = useState<number | 'check'>(0);

  useEffect(() => {
    let live = true;
    void (async () => {
      const res = await getTraining();
      if (!live) return;
      if (res.kind === 'ok') setPayload(res.body);
      else setLoadError(true);
    })();
    return () => {
      live = false;
    };
  }, []);

  return (
    <OverlayFrame dialogRef={dialogRef} onKeyDown={onKeyDown} onClose={onClose}>
      <AnimatePresence mode="wait" initial={false}>
        {loadError || payload === null ? (
          <TrainingShell
            key="pending"
            titleId="training-pending-title"
            footer={
              <DialogFooter>
                <Button variant="outline-square" size="xs" onClick={onClose}>
                  Close
                </Button>
              </DialogFooter>
            }
          >
            <div className={DIALOG_BODY}>
              {loadError ? (
                <DialogError message="Could not load the training right now. Please try again." />
              ) : (
                <p className="text-xs font-light text-text">Loading…</p>
              )}
            </div>
          </TrainingShell>
        ) : pos === 'check' ? (
          <KnowledgeCheck
            key="check"
            payload={payload}
            onProgress={onProgress}
            onBackToSteps={() => setPos(payload.steps.length - 1)}
            onClose={onClose}
          />
        ) : (
          <TrainingStepView
            key={`step-${pos}`}
            payload={payload}
            index={pos}
            onProgress={onProgress}
            onAdvance={(next) => setPos(next)}
          />
        )}
      </AnimatePresence>
    </OverlayFrame>
  );
}

/** The training dialog's shell: one title, one right-pinned position Pill, the caller's body/footer. */
function TrainingShell({
  titleId,
  position,
  footer,
  children,
}: {
  titleId: string;
  position?: string;
  footer: ReactNode;
  children: ReactNode;
}) {
  return (
    <ModalShell
      labelledBy={titleId}
      headerInset="title"
      header={
        <div className="flex flex-wrap items-center justify-between gap-7">
          <h1 id={titleId} className={MODAL_TITLE}>
            AI Awareness training
          </h1>
          {position && (
            <Pill tone="subtle" className="shrink-0">
              {position}
            </Pill>
          )}
        </div>
      }
      footer={footer}
    >
      {children}
    </ModalShell>
  );
}

function TrainingStepView({
  payload,
  index,
  onProgress,
  onAdvance,
}: {
  payload: TrainingPayload;
  index: number;
  onProgress: () => void;
  onAdvance: (next: number | 'check') => void;
}) {
  const step = payload.steps[index];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!step) return null;

  const isLast = index === payload.steps.length - 1;

  const next = async () => {
    setSaving(true);
    setError(null);
    const res = await completeStep(step.stepNo);
    setSaving(false);
    if (res.kind === 'ok') {
      onProgress();
      onAdvance(isLast ? 'check' : index + 1);
    } else if (res.kind === 'unauthorized') {
      setError('Your session has expired. Please sign in again.');
    } else {
      setError('Could not save your progress. Please try again.');
    }
  };

  return (
    <TrainingShell
      titleId={`training-step-${step.stepNo}-title`}
      position={`Step ${step.stepNo} of ${payload.steps.length}`}
      footer={
        <DialogFooter
          left={
            index > 0 ? (
              <Button variant="outline-square" size="xs" onClick={() => onAdvance(index - 1)}>
                Back
              </Button>
            ) : undefined
          }
        >
          <Button
            variant="outline-square"
            size="xs"
            state={saving ? 'disabled' : 'default'}
            onClick={() => void next()}
          >
            {saving ? 'Saving…' : isLast ? 'Start the check' : 'Next'}
          </Button>
        </DialogFooter>
      }
    >
      <div className={DIALOG_BODY}>
        <div>
          <h2 className="text-sm font-medium text-ink">{step.title}</h2>
          {/* Whether this step's wording follows your level — stated, because a step that adapts and
              one that does not must be tellable apart (LH-F09). */}
          <p className="mt-1 text-xs font-light text-text">
            {step.adaptsByLevel ? 'Tuned to your level' : 'The same for everyone'}
          </p>
        </div>
        <div className="grid max-w-[70ch] gap-5">
          {step.body.map((para, i) => (
            <p key={i} className="text-xs font-light text-text">
              {para}
            </p>
          ))}
        </div>
        {error && <DialogError message={error} />}
      </div>
    </TrainingShell>
  );
}

function KnowledgeCheck({
  payload,
  onProgress,
  onBackToSteps,
  onClose,
}: {
  payload: TrainingPayload;
  onProgress: () => void;
  onBackToSteps: () => void;
  onClose: () => void;
}) {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<{
    passed: boolean;
    scorePct: number;
    passPct: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { questions } = payload;
  const allAnswered = questions.every((q) => answers[q.id] !== undefined);

  const submit = async () => {
    // We send the CHOSEN OPTIONS, positionally, and nothing else (LH-F11). The browser holds no answer
    // key and does no scoring: the score, the pass mark and the verdict all come back from the server,
    // because this is the platform's mandatory training and a self-asserted pass is not a pass.
    const chosen = questions
      .map((q) => answers[q.id])
      .filter((oi): oi is number => oi !== undefined);
    if (chosen.length !== questions.length) return; // unreachable: Submit is disabled until all answered
    setSaving(true);
    setError(null);
    const res = await submitKnowledgeCheck(chosen);
    setSaving(false);
    if (res.kind === 'ok') {
      setResult({
        passed: res.body.passed,
        scorePct: res.body.scorePct,
        passPct: res.body.passPct,
      });
      onProgress();
    } else if (res.kind === 'unauthorized') {
      setError('Your session has expired. Please sign in again.');
    } else {
      setError('Could not submit your answers. Please try again.');
    }
  };

  if (result) {
    return (
      <TrainingShell
        titleId="training-result-title"
        position={result.passed ? 'You passed' : 'Not quite yet'}
        footer={
          <DialogFooter>
            {result.passed ? (
              <Button variant="outline-square" size="xs" onClick={onClose}>
                Done
              </Button>
            ) : (
              <Button
                variant="outline-square"
                size="xs"
                onClick={() => {
                  setResult(null);
                  setAnswers({});
                }}
              >
                Try again
              </Button>
            )}
          </DialogFooter>
        }
      >
        <div className={DIALOG_BODY}>
          {/* Only the server's own findings are rendered — the browser computes nothing (LH-F11). */}
          <p className="max-w-[70ch] text-xs font-light text-text">
            You scored {result.scorePct}%, and {result.passPct}% is the pass mark.{' '}
            {result.passed
              ? 'Your training is complete — thank you.'
              : 'Re-take the check as often as you need.'}
          </p>
        </div>
      </TrainingShell>
    );
  }

  return (
    <TrainingShell
      titleId="training-check-title"
      position="Knowledge check"
      footer={
        <DialogFooter
          left={
            <Button variant="outline-square" size="xs" onClick={onBackToSteps}>
              Back
            </Button>
          }
        >
          <Button
            variant="outline-square"
            size="xs"
            state={!allAnswered || saving ? 'disabled' : 'default'}
            onClick={() => void submit()}
          >
            {saving ? 'Submitting…' : 'Submit answers'}
          </Button>
        </DialogFooter>
      }
    >
      <div className={DIALOG_BODY}>
        <p className="max-w-[70ch] text-xs font-light text-text">
          You need {payload.passPct}% to pass, and you can re-take it as often as you need.
        </p>
        {questions.map((q, i) => (
          <RadioGroup
            key={q.id}
            name={q.id}
            legend={`${i + 1}. ${q.prompt}`}
            options={q.options.map((opt, oi) => ({
              label: opt,
              checked: answers[q.id] === oi,
              onSelect: () => setAnswers((a) => ({ ...a, [q.id]: oi })),
            }))}
          />
        ))}
        {error && <DialogError message={error} />}
      </div>
    </TrainingShell>
  );
}
